import React, { useState, useMemo } from "react";
import { Copy, Code, Lock, Unlock } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

function PublishEmbedTab({ getEmbedUrl, getEmbedCode }) {
    const { t } = useTranslation();
    const [requiresLogin, setRequiresLogin] = useState(true);

    const embedUrl = useMemo(() => getEmbedUrl(requiresLogin), [getEmbedUrl, requiresLogin]);
    const embedCode = useMemo(() => getEmbedCode(requiresLogin), [getEmbedCode, requiresLogin]);

    const copyCode = () => {
        if (!embedCode) return;
        navigator.clipboard.writeText(embedCode);
        toast.success(t("publishModal.embed.copySuccess"));
    };

    if (!getEmbedUrl(true) && !getEmbedUrl(false)) {
        return (
            <div className="py-8 text-center space-y-2">
                <Code className="w-8 h-8 text-muted-foreground/30 mx-auto" />
                <p className="text-sm font-medium text-muted-foreground">{t("publishModal.embed.noEmbedAvailable")}</p>
                <p className="text-xs text-muted-foreground max-w-[260px] mx-auto">
                    {t("publishModal.embed.setupDomainFirst")}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
                {t("publishModal.embed.description")}
            </p>

            <div className="relative flex bg-muted/60 rounded-lg p-0.5">
                <div
                    className="absolute top-0.5 bottom-0.5 rounded-md bg-background shadow-sm transition-all duration-200 ease-out"
                    style={{ width: "calc(50% - 2px)", left: requiresLogin ? "2px" : "calc(50% + 0px)" }}
                />
                <button
                    onClick={() => setRequiresLogin(true)}
                    className="relative z-10 flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors duration-200 rounded-md"
                >
                    <Lock className="w-3 h-3" />
                    {t("publishModal.embed.withLogin")}
                </button>
                <button
                    onClick={() => setRequiresLogin(false)}
                    className="relative z-10 flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors duration-200 rounded-md"
                >
                    <Unlock className="w-3 h-3" />
                    {t("publishModal.embed.withoutLogin")}
                </button>
            </div>

            <p className="text-[11px] text-muted-foreground leading-relaxed">
                {requiresLogin
                    ? t("publishModal.embed.withLoginHint")
                    : t("publishModal.embed.withoutLoginHint")}
            </p>

            <div className="space-y-2">
                <div className="flex items-center justify-end">
                    <Button variant="ghost" size="sm" onClick={copyCode} className="h-7 px-2.5 text-xs gap-1.5">
                        <Copy className="w-3 h-3" />
                        {t("common.copy")}
                    </Button>
                </div>
                <div className="bg-muted/50 border rounded-md px-3 py-3">
                    <code className="text-[11px] text-foreground break-all leading-relaxed font-mono">{embedCode}</code>
                </div>
            </div>
        </div>
    );
}

export default React.memo(PublishEmbedTab);
